import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { coverStamp, formatCertDate } from '../attestation';
import InsuranceAttestation from '../components/InsuranceAttestation';
import { TENANT_ID } from '../tenant';
import { useI18n, usePublicTenantLocale } from '../i18n';
import { normalizePlate } from '../wallets';
import { callFunction } from '../firebase';
import '../styles/attestation.css';

function rowToPolicy(row) {
    return {
        id: row.policyId,
        policyNumber: row.policyNumber,
        status: row.status,
        productName: row.cover,
        coverageType: row.cover,
        policyType: row.policyType,
        effectiveDate: row.validFrom,
        expiryDate: row.validTo,
        createdAt: row.issuedAt,
        ecowasBrownCard: row.ecowasBrownCard,
        vehicle: {
            registrationNumber: row.plate || row.plateDisplay,
            make: row.make,
            model: row.model,
            year: row.year,
            vin: row.vin,
            use: row.vehicleUse
        }
    };
}

function PoliceVerify() {
    const { token } = useParams();
    const { t } = useI18n();
    const [localeTenant, setLocaleTenant] = useState(TENANT_ID);
    usePublicTenantLocale(localeTenant);
    const [queryPlate, setQueryPlate] = useState('');
    const [row, setRow] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(!!token);
    const [searched, setSearched] = useState('');
    const [ussdText, setUssdText] = useState('');
    const [ussdReply, setUssdReply] = useState('CON Pulse Insurance *920#\n1. Verify plate\n2. Verify policy number');
    const [ussdBusy, setUssdBusy] = useState(false);
    const [ussdEntry, setUssdEntry] = useState('');

    const loadToken = async (id) => {
        const snap = await getDoc(doc(db, 'attestations', id));
        if (!snap.exists()) throw new Error('This QR is not a recognised Pulse Insurance certificate.');
        return snap.data();
    };

    useEffect(() => {
        if (!token) {
            setLoading(false);
            return;
        }
        setLoading(true);
        setError('');
        loadToken(token).then((data) => {
            setRow(data);
            if (data.tenantId) setLocaleTenant(data.tenantId);
            setLoading(false);
        }).catch((err) => {
            setError(err.message || 'Could not verify this certificate.');
            setLoading(false);
        });
    }, [token]);

    const searchPlate = async (e) => {
        e.preventDefault();
        const key = normalizePlate(queryPlate);
        if (key.length < 4) {
            setError('Enter a registration number such as BJL 4700 Z.');
            return;
        }
        setLoading(true);
        setError('');
        setRow(null);
        setSearched(queryPlate);
        try {
            const plateSnap = await getDoc(doc(db, 'plateIndex', key));
            if (!plateSnap.exists()) {
                setError('No Pulse certificate found for plate ' + queryPlate.toUpperCase() + '.');
                setLoading(false);
                return;
            }
            const idx = plateSnap.data();
            if (idx.token) {
                try {
                    const att = await loadToken(idx.token);
                    setRow(att);
                    if (att.tenantId) setLocaleTenant(att.tenantId);
                    setLoading(false);
                    return;
                } catch (err) {
                    setRow({
                        ...idx,
                        plate: idx.plateDisplay || idx.plate,
                        cover: idx.cover || 'Motor',
                        issuedAt: idx.updatedAt
                    });
                    setLoading(false);
                    return;
                }
            }
            setRow({
                ...idx,
                plate: idx.plateDisplay || idx.plate,
                cover: 'Motor'
            });
        } catch (err) {
            setError(err.message || 'Could not search plates.');
        }
        setLoading(false);
    };

    const policy = row ? rowToPolicy(row) : null;
    const stamp = policy ? coverStamp(policy) : null;

    return (
        <div className="att-shell">
            <div className="container px-0" style={{ maxWidth: 980 }}>
                <div className="text-white text-center mb-3">
                    <div style={{ fontWeight: 800, letterSpacing: '.08em' }}>{t('police.title')}</div>
                    <div className="small text-white-50">{t('police.subtitle')}</div>
                </div>

                <form className="card border-0 shadow-sm mb-3 no-print" onSubmit={searchPlate}>
                    <div className="card-body">
                        <label className="form-label fw-semibold">{t('police.searchLabel')}</label>
                        <div className="d-flex gap-2">
                            <input
                                className="form-control form-control-lg"
                                value={queryPlate}
                                onChange={(e) => setQueryPlate(e.target.value.toUpperCase())}
                                placeholder="BJL 4700 Z"
                                autoFocus={!token}
                            />
                            <button className="btn btn-warning btn-lg" type="submit" disabled={loading}>{t('police.check')}</button>
                        </div>
                    </div>
                </form>

                {loading && <div className="alert alert-light">{t('police.checking')}</div>}
                {error && (
                    <div className="alert alert-danger">
                        {error}
                        {searched ? <div className="small mt-1">{t('police.plateNotOnCert', { plate: searched.toUpperCase() })}</div> : null}
                    </div>
                )}
                {row && stamp && (
                    <>
                        <div className={'pv-banner pv-' + stamp.kind}>
                            {stamp.kind === 'ok' ? t('police.coverInForce') : stamp.kind === 'expired' ? t('police.notInForce') : stamp.label}
                            <div className="pv-sub">
                                {t('police.holderLine', { name: row.holderName, plate: row.plate || row.plateDisplay || '—', policy: row.policyNumber })}
                                {row.validTo ? t('police.toDate', { date: formatCertDate(row.validTo) }) : ''}
                            </div>
                        </div>
                        {row.token || row.policyId ? (
                            <InsuranceAttestation
                                companyName={row.companyName}
                                logoUrl={row.logoUrl}
                                tenantId={row.tenantId}
                                verifyMode
                                customer={{ fullName: row.holderName, nationalId: row.nationalId, phone: row.phone }}
                                policy={policy}
                            />
                        ) : null}
                    </>
                )}
                <div className="card border-0 shadow-sm mb-3 no-print">
                    <div className="card-body">
                        <div className="fw-semibold">USSD demo · *920#</div>
                        <p className="small text-muted mb-2">Africell / QCell / Gamcel shortcode needs a carrier contract. This menu uses the same plate check as police verify.</p>
                        <pre className="bg-dark text-success p-3 rounded small mb-2" style={{ whiteSpace: 'pre-wrap' }}>{ussdReply}</pre>
                        <div className="d-flex gap-2 flex-wrap mb-2">
                            <button type="button" className="btn btn-outline-secondary btn-sm" disabled={ussdBusy} onClick={async () => {
                                setUssdBusy(true);
                                try {
                                    const data = await callFunction('ussdDemo', { text: '' });
                                    setUssdReply((data && data.reply) || 'CON Pulse Insurance *920#');
                                    setUssdText('');
                                } catch (err) { setUssdReply('END Demo unavailable'); }
                                setUssdBusy(false);
                            }}>*920#</button>
                            <button type="button" className="btn btn-outline-secondary btn-sm" disabled={ussdBusy} onClick={async () => {
                                setUssdBusy(true);
                                try {
                                    const data = await callFunction('ussdDemo', { text: '1' });
                                    setUssdReply((data && data.reply) || '');
                                    setUssdText('1');
                                } catch (err) { setUssdReply('END Demo unavailable'); }
                                setUssdBusy(false);
                            }}>1 · Plate</button>
                        </div>
                        <form className="d-flex gap-2" onSubmit={async (e) => {
                            e.preventDefault();
                            const value = ussdEntry.trim();
                            if (!value) return;
                            const next = ussdText ? (ussdText + '*' + value) : value;
                            setUssdEntry('');
                            setUssdBusy(true);
                            try {
                                const data = await callFunction('ussdDemo', { text: next });
                                setUssdReply((data && (data.reply || data.message)) || 'END No reply');
                                setUssdText(next);
                            } catch (err) {
                                setUssdReply('END ' + (err.message || 'Demo unavailable'));
                            }
                            setUssdBusy(false);
                        }}>
                            <input className="form-control" value={ussdEntry} onChange={(e) => setUssdEntry(e.target.value.toUpperCase())} placeholder="BJL 4700 Z" />
                            <button className="btn btn-warning" type="submit" disabled={ussdBusy}>{t('police.check')}</button>
                        </form>
                        <div className="small text-white-50 mt-2">Webhook for later Africell / Africa&apos;s Talking bind: <code className="text-white">https://pulse-insurance.web.app/hooks/ussd</code> (shared secret header, same as mobile-money webhook).</div>
                    </div>
                </div>
                <div className="text-center mt-3 no-print">
                    <Link to="/client/login" className="text-white-50 small">{t('police.clientPortal')}</Link>
                </div>
            </div>
        </div>
    );
}

export default PoliceVerify;
